import styled, { css } from "styled-components";

export type ButtonVariant = "default" | "primary" | "ghost" | "danger";

const buttonStyles = css<{ $variant?: ButtonVariant }>`
  appearance: none;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme, $variant }) =>
    $variant === "primary"
      ? theme.accent
      : $variant === "danger"
        ? theme.danger
        : $variant === "ghost"
          ? "transparent"
          : theme.surface};
  color: ${({ theme, $variant }) =>
    $variant === "primary" || $variant === "danger" ? "white" : theme.ink};
  border-color: ${({ theme, $variant }) =>
    $variant === "primary"
      ? theme.accent
      : $variant === "danger"
        ? theme.danger
        : theme.border};
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  display: inline-block;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const AppShell = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
`;

export const TopbarRoot = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Brand = styled.div``;

export const BrandMark = styled.span`
  font-family: ${({ theme }) => theme.mono};
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const BrandTitle = styled.h1`
  margin: 0.15rem 0 0;
  font-family: ${({ theme }) => theme.display};
  font-size: 2rem;
  font-weight: 600;
`;

export const BrandSubtitle = styled.p`
  margin: 0.2rem 0 0;
  color: ${({ theme }) => theme.muted};
`;

export const UserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  img {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.border};
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`;

export const TabsNav = styled.nav`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.border : "transparent")};
  background: ${({ theme, $active }) =>
    $active ? theme.surface : "transparent"};
  color: ${({ theme, $active }) => ($active ? theme.ink : theme.muted)};
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  box-shadow: ${({ theme, $active }) => ($active ? theme.shadow : "none")};
`;

export const Button = styled.button<{ $variant?: ButtonVariant }>`
  ${buttonStyles}
`;

export const ButtonLink = styled.a<{ $variant?: ButtonVariant }>`
  ${buttonStyles}
`;

export const Panel = styled.section`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  padding: 1rem;
`;

export const Grid = styled.div<{ $cols?: 2 }>`
  display: grid;
  gap: 1rem;

  ${({ $cols }) =>
    $cols === 2 &&
    css`
      grid-template-columns: 1.1fr 0.9fr;

      @media (max-width: 860px) {
        grid-template-columns: 1fr;
      }
    `}
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const listItemBase = css`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  background: ${({ theme }) => theme.listItemBg};
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;

  h3 {
    margin: 0;
    font-size: 1rem;
  }

  p {
    margin: 0.2rem 0 0;
    color: ${({ theme }) => theme.muted};
    font-size: 0.9rem;
  }
`;

export const ListItem = styled.button<{ $active?: boolean }>`
  ${listItemBase}
  border-color: ${({ theme, $active }) =>
    $active ? theme.accent : theme.border};
  background: ${({ theme, $active }) =>
    $active ? theme.accentSoft : theme.listItemBg};
  cursor: pointer;
`;

export const ListItemStatic = styled.div`
  ${listItemBase}
  cursor: default;
`;

export const ListItemLink = styled.a`
  ${listItemBase}
  cursor: pointer;
  text-decoration: none;
  display: block;
`;

export const Badge = styled.span`
  display: inline-block;
  font-family: ${({ theme }) => theme.mono};
  font-size: 0.72rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.badgeBg};
  color: ${({ theme }) => theme.muted};
`;

export const Form = styled.form`
  display: grid;
  gap: 0.75rem;

  label {
    display: grid;
    gap: 0.3rem;
    font-size: 0.875rem;
  }

  input,
  textarea,
  select {
    font: inherit;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 8px;
    padding: 0.55rem 0.7rem;
    background: white;
  }

  textarea {
    min-height: 140px;
    resize: vertical;
    font-family: ${({ theme }) => theme.mono};
    font-size: 0.85rem;
  }
`;

export const Fieldset = styled.fieldset`
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;

  legend {
    padding: 0 0.35rem;
    font-weight: 600;
  }

  &:disabled {
    opacity: 0.65;
  }
`;

export const FormActions = styled.div`
  display: flex;
  gap: 0.65rem;
  align-items: center;
  flex-wrap: wrap;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const CheckboxRow = styled.label`
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
`;

export const LoginPageRoot = styled.div`
  min-height: calc(100vh - 3rem);
  display: grid;
  place-items: center;
`;

export const LoginCard = styled.div`
  width: min(420px, 100%);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadow};

  h2 {
    font-family: ${({ theme }) => theme.display};
    margin: 0.3rem 0 0.5rem;
  }
`;

export const ErrorBanner = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(155, 44, 44, 0.35);
  background: rgba(155, 44, 44, 0.08);
  color: ${({ theme }) => theme.danger};
  font-size: 0.875rem;
`;

export const SuccessBanner = styled.div`
  margin-top: 0.85rem;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(15, 107, 92, 0.35);
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accent};
  font-size: 0.875rem;
`;

export const StatusLine = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.muted};
  padding: 1rem;
`;

export const Muted = styled.p`
  margin: 0.2rem 0 0;
  color: ${({ theme }) => theme.muted};
`;

export const Detail = styled.div`
  pre {
    white-space: pre-wrap;
    font-family: ${({ theme }) => theme.mono};
    font-size: 0.82rem;
    background: ${({ theme }) => theme.detailPreBg};
    border-radius: 8px;
    padding: 0.85rem;
    border: 1px solid ${({ theme }) => theme.border};
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.muted};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.radius};
`;

export const SectionTitle = styled.h2``;

export const SectionSubtitle = styled.h3`
  margin-top: 1.5rem;
`;

export const MetaList = styled.dl`
  margin: 0;
  display: grid;
  gap: 0.75rem;

  div {
    display: grid;
    gap: 0.15rem;
  }

  dt {
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.muted};
  }

  dd {
    margin: 0;
    font-family: ${({ theme }) => theme.mono};
    font-size: 0.9rem;
  }
`;
